import { useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { MapPin } from "lucide-react";

interface VentureCardProps {
  name: string;
  location: string;
  price: string;
  features: string[];
  image: string;
  imageSmall: string;
}

const VentureCard = ({ name, location, price, features, image, imageSmall }: VentureCardProps) => {
  const [imageLoaded, setImageLoaded] = useState(false);

  return (
    <Card className="overflow-hidden border-border hover:shadow-xl transition-all group">
      <div className="relative h-64 overflow-hidden">
        {/* Skeleton shown while image loads */}
        {!imageLoaded && (
          <Skeleton className="absolute inset-0 w-full h-full" />
        )}
        <img 
          src={image} 
          srcSet={`${imageSmall} 400w, ${image} 800w`}
          sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw"
          alt={name} 
          loading="lazy"
          onLoad={() => setImageLoaded(true)}
          className={`w-full h-full object-cover group-hover:scale-110 transition-all duration-500 ${
            imageLoaded ? 'opacity-100' : 'opacity-0'
          }`}
        />
        <div className="absolute top-4 right-4">
          <Badge className="bg-accent text-accent-foreground">
            {price}
          </Badge>
        </div>
      </div>
      <CardContent className="p-6">
        <h3 className="text-2xl font-bold text-foreground mb-2">{name}</h3>
        <div className="flex items-center gap-2 text-muted-foreground mb-4">
          <MapPin className="h-4 w-4" />
          <span>{location}</span>
        </div>
        <div className="flex flex-wrap gap-2 mb-4">
          {features.map((feature, idx) => (
            <Badge key={idx} variant="outline" className="border-primary/20">
              {feature}
            </Badge>
          ))}
        </div>
        <Button asChild className="w-full">
          <Link to={`/projects/${name.toLowerCase().replace(/\s+/g, '-')}`}>
            View Details
          </Link>
        </Button>
      </CardContent>
    </Card>
  );
};

export default VentureCard;
